import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import { BLOG_POSTS, BlogTilePreview } from '../components/Blog.jsx';
import BlogTile from '../components/BlogTile';
import ServiceBlogSection from '../components/ServiceBlogSection';

const SERVICE_FAQ = [
  {
    question: 'How can I request a quote for your pressure washing services?',
    answer: `Requesting a quote is easy! You can reach out to us by calling our dedicated customer service team at (602) 730-0728, or send us an email at coppercanyonpressurewash@gmail.com. One of our friendly representatives will promptly get in touch with you to discuss your specific cleaning needs and provide you with a customized quote tailored to your requirements.`
  },
  {
    question: 'Is pressure washing safe for all surfaces?',
    answer: {
      intro: 'Generally yes, we specialize in cleaning a wide variety of surfaces, including:',
      list: [
        'Concrete driveways and sidewalks',
        'Asphalt driveways',
        'Brick and stone patios, walls, and walkways',
        'Pool decks and tile',
        'Wood decks and fences',
        'Commercial storefronts, parking lots, and buildings'
      ],
      outro: `If you're unsure about a particular surface, just let us know, and we can advise on the best cleaning method.`
    }
  },
  {
    question: 'Will pressure washing damage my plants or landscaping?',
    answer: `We take great care in protecting your landscaping. Our team will cover or avoid delicate plants and outdoor features to prevent damage. We also use environmentally friendly, non-toxic cleaning solutions to ensure the safety of your garden and surrounding area!`
  },
  {
    question: 'Do you offer discounts or promotions?',
    answer: `Yes! We often run promotions or offer discounts for new customers, repeat customers, or bundled services. Be sure to ask about any available deals when you schedule your service.`
  },
  {
    question: "Can pressure washing increase my home's value?",
    answer: `Yes! A clean, well-maintained exterior can greatly enhance your home's curb appeal, which is a key factor in increasing property value. Whether you're selling your home or just maintaining its appearance, pressure washing can make a significant difference.`
  },
  {
    question: 'How often should I have my property pressure washed?',
    answer: {
      intro: 'The frequency of pressure washing depends on various factors like weather, location, and the condition of your surfaces. For example:',
      list: [
        'Driveways and sidewalks typically need cleaning once a year or whenever they become visibly dirty.',
        'Dumpster Pads should be cleaned every month to prevent mold, mildew, and dirt buildup.',
        'Decks and patios should be cleaned annually to maintain their appearance and prevent weathering.'
      ],
      outro: `We recommend a free consultation to assess your property's specific needs and give you a tailored cleaning schedule.`
    }
  }
];

const PressureWashing = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <SEO 
      title="Pressure Washing Services – Copper Canyon Pressure Wash"
      description="Expert pressure washing services for your home. We use professional equipment and eco-friendly solutions to clean driveways, patios, decks, and more. Serving Phoenix and surrounding areas."
      type="Service"
    />
    
    <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Pressure Washing Banner">
      <picture>
        <source srcSet="/pressure_washing_hero.avif" type="image/avif" />
        <source srcSet="/pressure_washing_hero.webp" type="image/webp" />
      <img 
        src="/pressure_washing_hero.png" 
        alt="Professional pressure washing in action" 
          title="Professional Pressure Washing Services - Before and After Results"
        className="w-full h-full object-cover object-center" 
          width="1600"
          height="400"
      />
      </picture>
    </section>

    <div className="max-w-3xl mx-auto">
      <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Pressure Washing</h1>
      <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Pressure Washing Services</h2>
      <p className="mb-6 text-gray-700">
        At Copper Canyon Pressure Wash, we're committed to delivering exceptional pressure washing services across the greater Phoenix area, from our base in Scottsdale. We understand Arizona's desert challenges—like intense sun, dust storms, and hard water stains that can dull your property. Our team uses high-powered, gas-driven pressure washers with adjustable nozzles to clean everything from delicate stucco exteriors to tough concrete driveways, ensuring your home or business looks its best.
      </p>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-[#4a3f35]">Precision Cleaning</h3>
          </div>
          <p className="text-gray-700">We use pressure settings from 1,500 to 3,000 PSI, carefully adjusted for each surface, to ensure a deep clean without damage.</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h3 className="text-xl font-semibold text-[#4a3f35]">Hot Water Option</h3>
          </div>
          <p className="text-gray-700">Our hot water washing option tackles oil stains and grease on driveways, leaving surfaces pristine for up to a year.</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-[#4a3f35]">Reliable Service</h3>
          </div>
          <p className="text-gray-700">We're focused on earning your trust with consistent, on-time pressure washing services across the Phoenix area.</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <h3 className="text-xl font-semibold text-[#4a3f35]">Expert Solutions</h3>
          </div>
          <p className="text-gray-700">We've studied how to remove sun-baked dirt, mineral stains, and algae common to Arizona homes.</p>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Pressure Washing Process</h2>
      <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
        <div className="space-y-6">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
            <div>
              <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Thorough Inspection</h3>
              <p className="text-gray-700">We start with a thorough inspection of your property to identify problem areas like mineral buildup or desert grime.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
            <div>
              <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Eco-Friendly Pre-Treatment</h3>
              <p className="text-gray-700">Using biodegradable detergents, we pre-treat stains to break down dirt without harming your landscaping or the environment.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
            <div>
              <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Precision Cleaning</h3>
              <p className="text-gray-700">Our gas-driven pressure washers then clean with precision, using hot water for tough stains and cooler settings for delicate surfaces.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
            <div>
              <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Inspection</h3>
              <p className="text-gray-700">We finish with a final rinse and inspection to ensure every inch of your property shines.</p>
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Pressure Washing</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Surface-Specific Care</h3>
          <p className="text-gray-700">We select the right tools and settings—like using 1,500 PSI for fragile stucco or 3,000 PSI for concrete—to prevent costly damage such as etching or cracking.</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Versatile Cleaning</h3>
          <p className="text-gray-700">We know how to clean all types of surfaces, from stained concrete driveways to epoxy garage floors and older brick, ensuring each material gets the proper care.</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
          <p className="text-gray-700">Our expertise helps avoid painful lessons learned from inexperienced teams, like ruined paint or eroded mortar, saving you time and money.</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Best Practices</h3>
          <p className="text-gray-700">We stay educated on best practices, so your property gets a safe, effective clean tailored to Arizona's unique desert environment.</p>
        </div>
      </div>
    </div>
    <div className="max-w-3xl mx-auto">
      <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Pressure Washing!</h3>
        <p className="mb-4 text-gray-700">Contact us today for a special offer on our pressure washing services.</p>
        <Link 
          to="/contact" 
          className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
        >
          Get a Free Quote
        </Link>
      </div>
    </div>

    <div className="max-w-2xl mx-auto mt-12 mb-12">
      <MiniContactForm />
    </div>

    <FAQ items={SERVICE_FAQ} title="Frequently Asked Questions About Pressure Washing" />
    {/* Featured Blog Post Section */}
    <ServiceBlogSection blogSlugs={['cleaning-house-siding-tips', 'pressure-washing-benefits']} />
  </div>
);

export default PressureWashing; 